import { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { DATASET_NAMES, DatasetName } from "@forgepoint/shared";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { useDatasetExplorerStore } from "../datasetExplorerStore";

/** Datasets gated behind the "demand-forecasting" feature flag. */
const DEMAND_FORECASTING_DATASETS: ReadonlySet<DatasetName> = new Set([
  "demand_forecasts",
  "demand_forecast_lines",
  "locations",
]);

/** Human-readable labels for dataset names. */
const DATASET_LABELS: Record<DatasetName, string> = {
  quote_lines: "Quote Lines",
  quotes: "Quotes",
  items: "Items",
  item_components: "Item Components",
  item_operations: "Item Operations",
  operations: "Operations",
  companies: "Companies",
  people: "People",
  price_lists: "Price Lists",
  ship_to_addresses: "Ship-To Addresses",
  opportunities: "Opportunities",
  users: "Users",
  locations: "Locations",
  demand_forecasts: "Demand Forecasts",
  demand_forecast_lines: "Demand Forecast Lines",
};

/**
 * Dropdown selector for choosing which dataset to query.
 */
export default function DatasetPicker() {
  const selectedDataset = useDatasetExplorerStore((s) => s.selectedDataset);
  const setSelectedDataset = useDatasetExplorerStore(
    (s) => s.setSelectedDataset,
  );
  const schemas = useDatasetExplorerStore((s) => s.schemas);
  const currentSchema = schemas.find((s) => s.dataset === selectedDataset);
  const demandForecastingEnabled = useFeatureFlag("demand-forecasting");

  const visibleDatasets = useMemo(
    () =>
      demandForecastingEnabled
        ? DATASET_NAMES
        : DATASET_NAMES.filter((n) => !DEMAND_FORECASTING_DATASETS.has(n)),
    [demandForecastingEnabled],
  );

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Source
      </Label>
      <Select
        value={selectedDataset ?? ""}
        onValueChange={(value) => setSelectedDataset(value as DatasetName)}
      >
        <SelectTrigger className="bg-background">
          <SelectValue placeholder="Select a dataset..." />
        </SelectTrigger>
        <SelectContent>
          {visibleDatasets.map((name) => (
            <SelectItem key={name} value={name}>
              {DATASET_LABELS[name]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {currentSchema && (
        <p className="text-[11px] text-muted-foreground">
          {currentSchema.fields.length} fields
          {currentSchema.joins.length > 0 &&
            ` \u00B7 ${currentSchema.joins.length} join${currentSchema.joins.length !== 1 ? "s" : ""} available`}
        </p>
      )}
    </div>
  );
}
